import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, LogIn, UserPlus, RefreshCw, Loader2, AlertCircle,
  Coins, Globe, Swords, Server, Wand2, Wallet, Package, Mail, Shield, X, RotateCcw, Skull, Power,
} from 'lucide-react';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useToast } from '@/shared/contexts/ToastContext';
import { useConfirm } from '@/shared/components/ui/ConfirmDialog';
import {
  getDashboardStats, getTopPower, getTopRichest, getNationBalance, getTopClass,
  getAlzTotal, getAlzRates, getCashTotal, getServicesStatus, getWarChannelStatus, restartService, restartServer,
  type DashboardStats, type TopCharacter, type TopAccount, type NationBalance, type ClassCount,
  type AlzTotal, type AlzRates, type CashTotal, type ServicesStatus, type WarChannelStatus,
} from '@/shared/services/adminService';
import { getDpsRecords, type DpsRecord } from '@/shared/services/rankingService';
import { CLASSES_PT, CLASSES_PT_SHORT, NATION_COLORS, formatClass } from './labels';

const POLL_INTERVAL_MS = 5000;

export default function AdminDashboardPage() {
  const { account, hasFeature } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [topPower, setTopPower] = useState<TopCharacter[]>([]);
  const [topRichest, setTopRichest] = useState<TopAccount[]>([]);
  const [nation, setNation] = useState<NationBalance | null>(null);
  const [topClass, setTopClass] = useState<ClassCount[]>([]);
  const [dpsRecords, setDpsRecords] = useState<DpsRecord[]>([]);
  const [alz, setAlz] = useState<AlzTotal | null>(null);
  const [alzRates, setAlzRates] = useState<AlzRates | null>(null);
  const [cash, setCash] = useState<CashTotal | null>(null);
  const [showAlzModal, setShowAlzModal] = useState(false);
  const [servicesStatus, setServicesStatus] = useState<ServicesStatus | null>(null);
  const [showServicesModal, setShowServicesModal] = useState(false);
  const [warChannel, setWarChannel] = useState<WarChannelStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const fetchAll = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [s, tp, tr, n, tc, dps, a, rates, ct, svc, war] = await Promise.all([
        getDashboardStats(),
        getTopPower().catch(() => [] as TopCharacter[]),
        getTopRichest().catch(() => [] as TopAccount[]),
        getNationBalance().catch(() => null),
        getTopClass().catch(() => [] as ClassCount[]),
        getDpsRecords().catch(() => [] as DpsRecord[]),
        getAlzTotal().catch(() => null),
        getAlzRates().catch(() => null),
        getCashTotal().catch(() => null),
        getServicesStatus().catch(() => null),
        getWarChannelStatus().catch(() => null),
      ]);
      setStats(s);
      setTopPower(tp);
      setTopRichest(tr);
      setNation(n);
      setTopClass(tc);
      setDpsRecords(dps);
      setAlz(a);
      setAlzRates(rates);
      setCash(ct);
      setServicesStatus(svc);
      setWarChannel(war);
      setError(null);
      setLastUpdate(new Date());
    } catch (err: any) {
      setError(err?.message || 'Erro ao carregar estatisticas');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    const id = setInterval(() => fetchAll(true), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  // Reiniciar o servidor inteiro: lockout global de 60 s (fecha os jogos pelo NextGuard) + `cabal-compat restart`.
  const confirmDlg = useConfirm();
  const { notify } = useToast();
  const [restartingServer, setRestartingServer] = useState(false);
  const doServerRestart = async () => {
    const ok = await confirmDlg({
      title: 'Reiniciar o servidor',
      description: 'Todos os jogadores serão desconectados (aviso "Servidor reiniciando para atualizações", '
        + 'login bloqueado por 1 minuto) e todos os canais serão reiniciados. Leva alguns minutos para voltar. '
        + 'Evite durante a guerra (8, 12, 16, 18, 20 e 22h): o canal 21 cai junto.',
      tone: 'danger', confirmLabel: 'Reiniciar servidor',
    });
    if (!ok) return;
    setRestartingServer(true);
    try {
      const res = await restartServer();
      notify('success', res.message || 'Reinício do servidor iniciado');
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro ao reiniciar o servidor');
    } finally {
      setRestartingServer(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white">Painel Admin</h1>
          <p className="text-sm text-white/65 mt-1">
            Bem-vindo, <span className="text-[#00e1d7] font-semibold">{account?.userName || account?.id}</span>
            {lastUpdate && <> · atualizado {lastUpdate.toLocaleTimeString('pt-BR')}</>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchAll()}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                       bg-[#00e1d7]/10 border border-[#00e1d7]/25 text-[#00e1d7]
                       hover:bg-[#00e1d7]/20 transition cursor-pointer disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Atualizar
          </button>
          {hasFeature('restart_channel') && (
            <button
              onClick={doServerRestart}
              disabled={restartingServer}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                         bg-red-500/10 border border-red-500/30 text-red-400
                         hover:bg-red-500/20 transition cursor-pointer disabled:opacity-50"
            >
              {restartingServer ? <Loader2 size={14} className="animate-spin" /> : <Power size={14} />}
              Reiniciar servidor
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="surface-card rounded-xl p-4 mb-6 flex items-start gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-400">Falha ao carregar</p>
            <p className="text-xs text-white/65 mt-0.5">{error}</p>
          </div>
        </div>
      )}

      {/* Stat widgets — grid única full-width (até 5 por linha em telas largas) */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2.5 sm:gap-4 mb-6">
        <StatLink
          to="/admin/onlines"
          color="#00e1d7"
          title="Online In-Game"
          value={loading && !stats ? '—' : (stats?.onlineAccounts ?? 0).toLocaleString('pt-BR')}
          sub={stats?.onlineChars != null
            ? `${stats.onlineChars.toLocaleString('pt-BR')} chars · ${(stats?.onlineGuard ?? 0).toLocaleString('pt-BR')} no guard`
            : 'contas distintas'}
          icon={<Users size={20} />}
        />
        <StatLink
          to="/admin/guard?loc=dungeon"
          color="#f59e0b"
          title="Em Dungeon"
          value={loading && !stats ? '—' : (stats?.onlineDungeon ?? 0).toLocaleString('pt-BR')}
          sub={stats
            ? (stats.onlineWar > 0
                ? `${stats.onlineWar.toLocaleString('pt-BR')} em guerra · clique p/ filtrar`
                : 'jogadores em dungeon · clique p/ filtrar')
            : 'jogadores em dungeon'}
          icon={<Skull size={20} />}
        />
        <StatLink
          to="/admin/accounts?todayOnly=1"
          color="#a855f7"
          title="Logins Hoje"
          value={loading && !stats ? '—' : (stats?.loginsToday ?? 0).toLocaleString('pt-BR')}
          sub="contas que entraram hoje"
          icon={<LogIn size={20} />}
        />
        <StatLink
          to="/admin/accounts?createdToday=1"
          color="#fa3a3a"
          title="Criadas Hoje"
          value={loading && !stats ? '—' : (stats?.createdToday ?? 0).toLocaleString('pt-BR')}
          sub="novas contas registradas"
          icon={<UserPlus size={20} />}
        />
        {/* Economia: Alz total + criado por dia/hora */}
        <StatButton
          color="#ffd700"
          title="Alz Total In-Game"
          value={loading && !alz ? '—' : compactAlz(alz?.alzTotal ?? 0)}
          sub={alz ? 'clique para ver o detalhamento' : 'somando fontes...'}
          icon={<Coins size={20} />}
          onClick={() => alz && setShowAlzModal(true)}
        />
        <StatCard
          color={alzRates && alzRates.perDay < 0 ? '#ef4444' : '#00e1d7'}
          title="Alz por Dia"
          value={alzRates ? (alzRates.perDay >= 0 ? '+' : '') + compactAlz(alzRates.perDay) : '—'}
          sub={alzRates ? (alzRates.perDay < 0 ? 'DIMINUIU nas últimas 24h (líquido)' : 'criado nas últimas 24h (líquido)') : 'acumulando...'}
          icon={<Coins size={20} />}
        />
        <StatCard
          color={alzRates && alzRates.perHour < 0 ? '#ef4444' : '#22c55e'}
          title="Alz por Hora"
          value={alzRates ? (alzRates.perHour >= 0 ? '+' : '') + compactAlz(alzRates.perHour) : '—'}
          sub={alzRates ? (alzRates.perHour < 0 ? 'DIMINUIU na última hora (líquido)' : 'criado na última hora (líquido)') : 'aguardando 1ª hora...'}
          icon={<Coins size={20} />}
        />
        {/* Cash + Infra */}
        <StatCard
          color="#f9a8d4"
          title="Cash In Game"
          value={cash ? compactAlz(cash.cashTotal) : '—'}
          sub={cash
            ? `conta ${compactAlz(cash.cashAccounts)} · item ${compactAlz(cash.cashInItems)}`
            : 'somando...'}
          icon={<Wallet size={20} />}
        />
        <StatButton
          color={servicesStatus ? (servicesStatus.down === 0 ? '#10b981' : '#f59e0b') : '#a855f7'}
          title="Canais Ligados"
          value={servicesStatus
            ? `${servicesStatus.channelsUp}/${servicesStatus.channelsTotal}`
            : '—'}
          sub={servicesStatus
            ? (servicesStatus.down === 0
                ? 'clique para ver todos os serviços'
                : `${servicesStatus.down} serviço(s) down — clique p/ ver`)
            : 'carregando...'}
          icon={<Server size={20} />}
          onClick={() => servicesStatus && setShowServicesModal(true)}
        />
        <StatBadge
          color={warChannel?.isOnline ? '#fa3a3a' : '#6b7280'}
          title="Canal Guerra"
          value={warChannel
            ? (warChannel.isOnline
                ? warChannel.total.toLocaleString('pt-BR')
                : 'OFF')
            : '—'}
          sub={warChannel && warChannel.isOnline
            ? `${warChannel.procyon} Procyon · ${warChannel.capella} Capella`
            : (warChannel ? 'canal 4 offline' : 'carregando...')}
          icon={<Wand2 size={20} />}
        />
      </div>

      <AlzBreakdownModal alz={alz} open={showAlzModal} onClose={() => setShowAlzModal(false)} />
      <ServicesModal status={servicesStatus} open={showServicesModal} onClose={() => setShowServicesModal(false)}
        canRestart={hasFeature('restart_channel')} onRestarted={() => fetchAll(true)} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <NationDonut data={nation} />
        <TopClassChart data={topClass} />
      </div>

      <div className="mb-6">
        <DpsRecordsChart data={dpsRecords} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TopList
          title="Top 10 — Poder de Combate"
          icon={<Swords size={14} className="text-amber-400" />}
          items={topPower}
          renderRight={(c) => (
            <div className="text-right">
              <p className="text-amber-400 font-bold text-sm">
                {(c.power ?? 0).toLocaleString('pt-BR')}
              </p>
              <p className="text-[10px] text-white/45">
                ATK {(c.pveAttack ?? 0).toLocaleString('pt-BR')}
                {' · '}
                DEF {(c.pveDefense ?? 0).toLocaleString('pt-BR')}
              </p>
            </div>
          )}
        />
        <TopList
          title="Top 10 — Contas Mais Ricas"
          icon={<Coins size={14} className="text-[#00e1d7]" />}
          items={topRichest}
          renderRight={(c) => (
            <div className="text-right">
              <p className="text-[#00e1d7] font-bold text-sm">
                {(c.totalAlz ?? 0).toLocaleString('pt-BR')} alz
              </p>
              <p className="text-[10px] text-white/45">
                bolso {(c.charsAlz ?? 0).toLocaleString('pt-BR')}
                {' · '}
                baú {(c.warehouseAlz ?? 0).toLocaleString('pt-BR')}
              </p>
            </div>
          )}
        />
      </div>

      {stats?.note && (
        <div className="surface-card rounded-xl p-4 mt-6 flex items-start gap-3">
          <AlertCircle size={16} className="text-yellow-400 shrink-0 mt-0.5" />
          <p className="text-xs text-white/65 leading-relaxed">{stats.note}</p>
        </div>
      )}
    </div>
  );
}

/* ─────────────────── helpers ─────────────────── */

/** Compacta numeros grandes de Alz: 12.3B / 7.4T / 850M */
function compactAlz(n: number): string {
  if (!n) return '0';
  const neg = n < 0;               // NEGATIVO = economia diminuiu (mostra com '-')
  const abs = Math.abs(n);
  const fmt =
    abs >= 1e15 ? (abs / 1e15).toFixed(2) + 'Q' :   // quadrilhao
    abs >= 1e12 ? (abs / 1e12).toFixed(2) + 'T' :   // trilhao
    abs >= 1e9  ? (abs / 1e9 ).toFixed(2) + 'B' :   // bilhao
    abs >= 1e6  ? (abs / 1e6 ).toFixed(2) + 'M' :
    abs >= 1e3  ? (abs / 1e3 ).toFixed(1) + 'K' :
    abs.toLocaleString('pt-BR');
  return (neg ? '-' : '') + fmt;
}

/* ─────────────────── components ─────────────────── */

function StatButton({
  color, title, value, sub, icon, onClick,
}: { color: string; title: string; value: string; sub: string; icon: React.ReactNode; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="stat-widget block hover:scale-[1.01] transition w-full text-left cursor-pointer"
      style={{ ['--c' as any]: color }}
    >
      <div className="flex items-start justify-between relative z-10">
        <div className="min-w-0">
          <p className="text-[11px] font-bold text-white/75 uppercase tracking-wider">{title}</p>
          <p className="text-3xl font-black text-white mt-1 leading-none truncate">{value}</p>
          <p className="text-[11px] mt-1.5 font-semibold" style={{ color }}>{sub}</p>
        </div>
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 border"
          style={{ background: `${color}20`, borderColor: `${color}30`, color }}
        >
          {icon}
        </div>
      </div>
    </button>
  );
}

function StatCard({
  color, title, value, sub, icon,
}: { color: string; title: string; value: string; sub: string; icon: React.ReactNode }) {
  return (
    <div className="stat-widget block" style={{ ['--c' as any]: color }}>
      <div className="flex items-start justify-between relative z-10">
        <div className="min-w-0">
          <p className="text-[11px] font-bold text-white/75 uppercase tracking-wider">{title}</p>
          <p className="text-3xl font-black text-white mt-1 leading-none truncate">{value}</p>
          <p className="text-[11px] mt-1.5 font-semibold" style={{ color }}>{sub}</p>
        </div>
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 border"
          style={{ background: `${color}20`, borderColor: `${color}30`, color }}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function StatBadge({
  color, title, value, sub, icon,
}: { color: string; title: string; value: string; sub: string; icon: React.ReactNode }) {
  return (
    <div className="stat-widget block opacity-70" style={{ ['--c' as any]: color }}>
      <div className="flex items-start justify-between relative z-10">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-[11px] font-bold text-white/75 uppercase tracking-wider">{title}</p>
            <span className="px-1.5 py-0.5 rounded text-[9px] font-extrabold uppercase tracking-wider bg-white/10 text-white/55 border border-white/15">
              WIP
            </span>
          </div>
          <p className="text-3xl font-black text-white/40 mt-1 leading-none">{value}</p>
          <p className="text-[11px] mt-1.5 font-semibold text-white/55">{sub}</p>
        </div>
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 border"
          style={{ background: `${color}15`, borderColor: `${color}25`, color: `${color}99` }}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function AlzBreakdownModal({
  alz, open, onClose,
}: { alz: AlzTotal | null; open: boolean; onClose: () => void }) {
  if (!open || !alz) return null;

  const total = alz.alzTotal || 1;
  const sources = [
    { key: 'inv',   label: 'Inventário dos Personagens', sub: 'Alz "no bolso" — atualizado pelo heartbeat em quem está online', value: alz.alzInventory, color: '#fa3a3a', icon: <Wallet  size={16} /> },
    { key: 'wh',    label: 'Armazéns Pessoais',          sub: 'Alz guardado no Warehouse de cada conta',                       value: alz.alzWarehouse, color: '#00e1d7', icon: <Package size={16} /> },
    { key: 'mail',  label: 'E-mails Pendentes',          sub: 'Alz despachado por correio que ainda não foi recolhido',        value: alz.alzMail,      color: '#a855f7', icon: <Mail    size={16} /> },
    { key: 'guild', label: 'Baús das Guildas',           sub: 'Alz parado no armazém compartilhado das guilds',                value: alz.alzGuild,     color: '#ffd700', icon: <Shield  size={16} /> },
  ];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onMouseDown={onClose} />
      <div
        className="relative z-10 w-full max-w-lg rounded-2xl border border-[#ffd700]/25 bg-[#0a1218]/95 backdrop-blur-xl shadow-[0_0_60px_rgba(255,215,0,0.08)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b border-white/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#ffd700]/15 border border-[#ffd700]/30 flex items-center justify-center text-[#ffd700]">
              <Coins size={20} />
            </div>
            <div>
              <h3 className="text-lg font-extrabold text-white">Alz Total In-Game</h3>
              <p className="text-xs text-white/55">Detalhamento por fonte de armazenamento</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-white/55 hover:text-white hover:bg-white/5 transition cursor-pointer"
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-3">
          {sources.map((s) => {
            const pct = (s.value / total) * 100;
            return (
              <div key={s.key} className="rounded-xl border border-white/[0.06] bg-[#0d141b] p-3.5">
                <div className="flex items-start gap-3">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 border"
                    style={{ background: `${s.color}18`, borderColor: `${s.color}40`, color: s.color }}
                  >
                    {s.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-3">
                      <p className="text-sm font-bold text-white truncate">{s.label}</p>
                      <p className="text-sm font-extrabold shrink-0" style={{ color: s.color }}>
                        {compactAlz(s.value)}
                      </p>
                    </div>
                    <p className="text-[10px] text-white/55 mt-0.5">{s.sub}</p>
                    <div className="mt-2 h-1.5 w-full bg-white/[0.04] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${Math.min(100, pct)}%`, background: s.color }}
                      />
                    </div>
                    <p className="text-[10px] text-white/45 mt-1 font-mono">
                      {s.value.toLocaleString('pt-BR')} alz · {pct.toFixed(1)}%
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="px-5 py-4 border-t border-white/5 flex items-center justify-between">
          <p className="text-xs text-white/65 uppercase tracking-wider font-bold">Total</p>
          <p className="text-2xl font-black text-[#ffd700]">{compactAlz(alz.alzTotal)}</p>
        </div>
        <p className="px-5 pb-4 text-[10px] text-white/40 font-mono text-right">
          {alz.alzTotal.toLocaleString('pt-BR')} alz
        </p>
      </div>
    </div>
  );
}

function StatLink({
  to, color, title, value, sub, icon,
}: { to: string; color: string; title: string; value: string; sub: string; icon: React.ReactNode }) {
  return (
    <Link to={to} className="stat-widget block hover:scale-[1.01] transition" style={{ ['--c' as any]: color }}>
      <div className="flex items-start justify-between relative z-10">
        <div>
          <p className="text-[11px] font-bold text-white/75 uppercase tracking-wider">{title}</p>
          <p className="text-3xl font-black text-white mt-1 leading-none">{value}</p>
          <p className="text-[11px] mt-1.5 font-semibold" style={{ color }}>{sub}</p>
        </div>
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 border"
          style={{ background: `${color}20`, borderColor: `${color}30`, color }}
        >
          {icon}
        </div>
      </div>
    </Link>
  );
}

function NationDonut({ data }: { data: NationBalance | null }) {
  const procyon = data?.procyon ?? 0;
  const capella = data?.capella ?? 0;
  const neutral = data?.neutral ?? 0;
  const total = procyon + capella + neutral || 1;

  const r = 42, cx = 60, cy = 60, c = 2 * Math.PI * r;
  const procyonLen = (procyon / total) * c;
  const capellaLen = (capella / total) * c;

  return (
    <div className="surface-card rounded-xl p-5">
      <h3 className="text-sm font-bold text-white/85 uppercase tracking-wider mb-4 flex items-center gap-2">
        <Globe size={14} className="text-[#00e1d7]" /> Equilíbrio de Nações
      </h3>
      <div className="flex items-center gap-6">
        <svg width={120} height={120} className="shrink-0">
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="#1a242c" strokeWidth={14} />
          <circle
            cx={cx} cy={cy} r={r} fill="none"
            stroke={NATION_COLORS[2]} strokeWidth={14}
            strokeDasharray={`${procyonLen} ${c - procyonLen}`}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
          <circle
            cx={cx} cy={cy} r={r} fill="none"
            stroke={NATION_COLORS[1]} strokeWidth={14}
            strokeDasharray={`${capellaLen} ${c - capellaLen}`}
            strokeDashoffset={-procyonLen}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        </svg>
        <div className="flex-1 space-y-2.5">
          <NationLegend color={NATION_COLORS[2]} name="Procyon" count={procyon} total={total} />
          <NationLegend color={NATION_COLORS[1]} name="Capella" count={capella} total={total} />
          {neutral > 0 && <NationLegend color="#6b7280" name="Neutro" count={neutral} total={total} />}
        </div>
      </div>
    </div>
  );
}

function NationLegend({ color, name, count, total }: { color: string; name: string; count: number; total: number }) {
  const pct = (count / total) * 100;
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm font-semibold text-white flex items-center gap-2">
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
        {name}
      </span>
      <span className="text-xs text-white/55">{count.toLocaleString('pt-BR')} ({pct.toFixed(1)}%)</span>
    </div>
  );
}

function TopClassChart({ data }: { data: ClassCount[] }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="surface-card rounded-xl p-5">
      <h3 className="text-sm font-bold text-white/85 uppercase tracking-wider mb-4">
        Distribuição por Classe
      </h3>
      {data.length === 0 ? (
        <p className="text-xs text-white/40 py-6 text-center">Sem dados</p>
      ) : (
        <div className="flex items-end justify-around gap-2 h-32">
          {data.slice(0, 9).map((d) => {
            const h = (d.count / max) * 100;
            const fullLabel = CLASSES_PT[d.classId] || `Cl ${d.classId}`;
            const shortLabel = CLASSES_PT_SHORT[d.classId] || `Cl${d.classId}`;
            return (
              <div key={d.classId} className="flex-1 flex flex-col items-center gap-1.5 min-w-0">
                <span className="text-[10px] font-bold text-white/65">{d.count}</span>
                <div
                  className="w-full bg-amber-500 rounded-t transition-all"
                  style={{ height: `${h}%`, minHeight: 4 }}
                />
                <span className="text-[9px] text-white/55 text-center truncate w-full" title={fullLabel}>
                  {shortLabel}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// DpsRecordsChart — grafico full-width dos recordes de DPS (feature nova).
// 9 classes x 2 barras (PvE ciano / PvP vermelho), altura relativa ao maior
// recorde. Hover mostra detentor + DPS + maior hit. Fonte: /Ranking/dpsRecords
// (_39_DpsRecords, alimentada pelo GH_DPSREC do GuardHub).
// =============================================================================

const DPS_CLASS_IDS = [1, 2, 3, 4, 5, 6, 7, 8, 9];
const DPS_PVE_COLOR = '#00e1d7';
const DPS_PVP_COLOR = '#ff6b6b';

function DpsRecordsChart({ data }: { data: DpsRecord[] }) {
  const byClass: Record<number, { pve?: DpsRecord; pvp?: DpsRecord }> = {};
  for (const r of data) (byClass[r.classId] ||= {})[r.mode === 1 ? 'pvp' : 'pve'] = r;
  const maxDps = Math.max(1, ...data.map((r) => r.bestDps));
  const hasAny = data.some((r) => r.bestDps > 0);

  return (
    <div className="surface-card rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-white/85 uppercase tracking-wider flex items-center gap-2">
          <Swords size={14} className="text-[#00e1d7]" /> Recordes de DPS por Classe
        </h3>
        <div className="flex items-center gap-4 text-[11px] font-semibold">
          <span className="flex items-center gap-1.5 text-white/65">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: DPS_PVE_COLOR }} /> PvE
          </span>
          <span className="flex items-center gap-1.5 text-white/65">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: DPS_PVP_COLOR }} /> PvP
          </span>
        </div>
      </div>
      {!hasAny ? (
        <p className="text-xs text-white/40 py-8 text-center">
          Sem recordes ainda — aguardando telemetria do guard
        </p>
      ) : (
        <div className="flex items-end justify-around gap-2 md:gap-4 h-44">
          {DPS_CLASS_IDS.map((cid) => {
            const bars = [
              { rec: byClass[cid]?.pve, color: DPS_PVE_COLOR, label: 'PvE' },
              { rec: byClass[cid]?.pvp, color: DPS_PVP_COLOR, label: 'PvP' },
            ];
            const fullLabel = CLASSES_PT[cid] || `Cl ${cid}`;
            return (
              <div key={cid} className="flex-1 flex flex-col items-center gap-1.5 min-w-0 h-full">
                <div className="flex items-end justify-center gap-1 md:gap-1.5 flex-1 w-full min-h-0">
                  {bars.map(({ rec, color, label }) => {
                    const dps = rec?.bestDps ?? 0;
                    const h = (dps / maxDps) * 100;
                    return (
                      <div
                        key={label}
                        className="group relative flex-1 max-w-[26px] h-full flex flex-col justify-end cursor-help"
                      >
                        {dps > 0 && (
                          <span className="text-[9px] font-bold text-white/65 text-center mb-0.5 truncate">
                            {compactAlz(dps)}
                          </span>
                        )}
                        <div
                          className="rounded-t transition-all duration-300 group-hover:brightness-125"
                          style={{
                            height: `${dps > 0 ? Math.max(h, 3) : 0}%`,
                            minHeight: dps > 0 ? 4 : 0,
                            background: `linear-gradient(180deg, ${color} 0%, ${color}55 100%)`,
                            boxShadow: dps > 0 ? `0 0 8px ${color}55` : 'none',
                          }}
                        />
                        {dps > 0 && (
                          <div
                            className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-50 whitespace-nowrap rounded-lg bg-black/95 px-3 py-2 text-xs"
                            style={{ border: `1px solid ${color}66` }}
                          >
                            <div className="font-bold" style={{ color }}>
                              {label} — {fullLabel}
                            </div>
                            <div className="text-white/90 font-semibold">{rec?.holderName || '—'}</div>
                            <div className="text-white/60">
                              DPS <span className="font-mono text-white">{dps.toLocaleString('pt-BR')}</span>
                              {' · '}maior hit{' '}
                              <span className="font-mono text-white">
                                {(rec?.bestSingleHit ?? 0).toLocaleString('pt-BR')}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <span
                  className="text-[9px] md:text-[10px] text-white/55 text-center truncate w-full shrink-0"
                  title={fullLabel}
                >
                  {CLASSES_PT_SHORT[cid] || `Cl${cid}`}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Item minimo que o TopList sabe renderizar — TopCharacter e TopAccount batem.
interface TopListItem {
  characterIdx?: number | null;
  userNum?: number | null;
  name?: string | null;
  lev?: number | null;
  ov?: number | null;
  charClass?: number | null;
  nation?: number | null;
  guildName?: string | null;
}

function TopList<T extends TopListItem>({
  title, icon, items, renderRight,
}: {
  title: string;
  icon: React.ReactNode;
  items: T[];
  renderRight: (c: T) => React.ReactNode;
}) {
  const rankColors = ['text-amber-400', 'text-slate-300', 'text-orange-400'];
  return (
    <div className="surface-card rounded-xl p-5">
      <h3 className="text-sm font-bold text-white/85 uppercase tracking-wider mb-4 flex items-center gap-2">
        {icon} {title}
      </h3>
      {items.length === 0 ? (
        <div className="flex items-center justify-center py-8 text-white/30">
          <Loader2 size={18} className="animate-pulse" />
        </div>
      ) : (
        <div className="space-y-1.5">
          {items.map((c, i) => {
            const url = c.userNum != null ? `/admin/account/${c.userNum}` : null;
            const inner = (
              <div className="flex items-center gap-3 px-3 py-2 rounded-lg
                              bg-[#0d141b] border border-white/[0.04] hover:bg-white/[0.02] transition">
                <span className={`text-base font-black w-6 text-center ${rankColors[i] || 'text-white/30'}`}>
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-1.5">
                    <p className="text-sm font-bold text-white truncate">{c.name || '—'}</p>
                    {c.lev != null && <span className="text-[10px] text-white/55 shrink-0">Lv.{c.lev}</span>}
                    {c.ov ? <span className="text-[10px] text-amber-400 shrink-0">OV {c.ov}</span> : null}
                  </div>
                  <p className="text-[10px] text-white/45 truncate">
                    {formatClass(c.charClass)}
                    {c.nation === 1 ? ' · Capella' : c.nation === 2 ? ' · Procyon' : ''}
                    {c.guildName ? ` · «${c.guildName}»` : ''}
                  </p>
                </div>
                {renderRight(c)}
              </div>
            );
            const rowKey = c.characterIdx ?? c.userNum ?? i;
            return url ? (
              <Link key={rowKey} to={url} className="block cursor-pointer">{inner}</Link>
            ) : (
              <div key={rowKey}>{inner}</div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// ServicesModal — drill-in dos serviços systemd (Canais + Agents + Master)
// =============================================================================

function ServicesModal({ status, open, onClose, canRestart, onRestarted }: {
  status: ServicesStatus | null;
  open: boolean;
  onClose: () => void;
  canRestart: boolean;
  onRestarted: () => void;
}) {
  const { notify } = useToast();
  const confirm = useConfirm();
  const [busyName, setBusyName] = useState<string | null>(null);

  if (!open || !status) return null;

  const channels = status.services.filter(s => s.isChannel);
  const others   = status.services.filter(s => !s.isChannel);

  const doRestart = async (name: string) => {
    const isChannel = /^WorldSvr_01_/.test(name);
    const ok = await confirm({
      title: `Reiniciar ${name}`,
      description: isChannel
        ? `Reiniciar o canal ${name}? Todos os jogadores nesse canal serão desconectados.`
        : `Reiniciar o serviço ${name}?`,
      tone: 'danger', confirmLabel: 'Reiniciar',
    });
    if (!ok) return;
    setBusyName(name);
    try {
      const res = await restartService(name);
      notify('success', res.message || `${name} reiniciado`);
      onRestarted();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally {
      setBusyName(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="surface-card rounded-2xl p-6 max-w-xl w-full max-h-[80vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Server size={18} className="text-purple-400" />
              Serviços Cabal
            </h3>
            <p className="text-[11px] text-white/50 mt-0.5">
              {status.up}/{status.total} ativos · canais: {status.channelsUp}/{status.channelsTotal}
              {canRestart && <span className="text-amber-400/80"> · ⟳ reinicia (só super-GM)</span>}
            </p>
          </div>
          <button onClick={onClose} className="text-white/55 hover:text-white cursor-pointer">
            <X size={18} />
          </button>
        </div>

        {/* Canais */}
        <div className="mb-4">
          <p className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-white/55 mb-2">Canais</p>
          <div className="grid grid-cols-2 gap-1.5">
            {channels.map(s => (
              <ServiceItem key={s.name} s={s}
                onRestart={canRestart ? () => doRestart(s.name) : undefined}
                busy={busyName === s.name} />
            ))}
          </div>
        </div>

        {/* Outros */}
        {others.length > 0 && (
          <div>
            <p className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-white/55 mb-2">Master + Agents</p>
            <div className="grid grid-cols-1 gap-1.5">
              {others.map(s => (
                <ServiceItem key={s.name} s={s}
                  onRestart={canRestart ? () => doRestart(s.name) : undefined}
                  busy={busyName === s.name} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ServiceItem({ s, onRestart, busy }: {
  s: import('@/shared/services/adminService').ServiceRow;
  onRestart?: () => void;
  busy?: boolean;
}) {
  // Cabal services rodam Type=simple e o filho forka -> SubState=exited.
  // 'active' (qualquer sub) ja conta como UP.
  const isUp = s.active === 'active';
  return (
    <div className={`flex items-center justify-between px-3 py-2 rounded-lg border
                     ${isUp
                       ? 'bg-emerald-500/[0.04] border-emerald-500/15'
                       : 'bg-red-500/[0.06] border-red-500/25'}`}>
      <div className="flex items-center gap-2 min-w-0">
        <span className={`w-2 h-2 rounded-full ${isUp ? 'bg-emerald-400' : 'bg-red-400'}`}
              style={{ boxShadow: `0 0 6px ${isUp ? '#10b981' : '#ef4444'}` }} />
        <span className={`text-xs font-bold truncate ${isUp ? 'text-white/85' : 'text-red-300'}`}>
          {s.name}
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className={`text-[10px] font-mono ${isUp ? 'text-white/45' : 'text-red-300/70'}`}>
          {isUp ? (s.pid ? `pid ${s.pid}` : 'up') : `${s.active}/${s.sub}`}
        </span>
        {onRestart && (
          <button onClick={onRestart} disabled={busy} title="Reiniciar serviço"
            className="p-1 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-400 hover:bg-amber-500/20 transition cursor-pointer disabled:opacity-50">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
          </button>
        )}
      </div>
    </div>
  );
}
